import { select } from "@angular-redux/store";
import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { Observable } from "rxjs/Observable";
import { MsalService } from "../../../../msal";
import { ApplicationUser } from "../../../../pb-screen/models/application.user.interface";
import { PlanningBlock } from "../../../../pb-screen/models/planning-block/planning-block.interface";
import { PoolService } from "../../../../pool/pool.service";
import { ComponentSource, ModalType } from "../../../models/component-source";
import { ModalManagerService } from "../../../services/modal-manager.service";
import { SharedService } from "../../../services/shared.service";
import { SharedActions } from "../../../shared.actions";
import { ContainerReuseStatus } from "../container-reuse-modal/container-reuse.state.enum";
import { UserFeedbackService } from "../../../../services/user-feedback.service";
import {AddressInfo} from "../../../models/container-pool-request/addressInfo";

export interface MoveToPoolModalInput {
  source: string;
  selectedPlanningBlocks?: PlanningBlock[];
}

@Component({
  selector: "move-to-pool-modal",
  templateUrl: "move-to-pool-modal.component.html",
})
export class MoveToPoolComponent extends SimpleModalComponent<MoveToPoolModalInput, boolean> implements OnInit, OnDestroy {

  constructor(
    private readonly sharedActions: SharedActions,
    private readonly sharedService: SharedService,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly poolService: PoolService,
    private readonly modalManager: ModalManagerService,
    private readonly authService: MsalService
  ) {
    super();
  }
  private destroy$ = new Subject<void>();
  public source: string;
  public onMoveToPoolSucceeded: EventEmitter<boolean> = new EventEmitter<boolean>();
  public isPendingRequest: boolean = false;
  public selectedContainerPoolRequestInfoStatus: number = 0;
  @select("applicationUser") applicationUser$: Observable<ApplicationUser>;

  public enableAutocomplete: boolean = false;
  public selectedPlanningBlocks: PlanningBlock[] = [];

  public componentSource = ComponentSource;
  public modalType = ModalType;
  public planDate: string;
  public loading = false;
  public addressList: AddressInfo[] = [];
  public selectedAdrsNumber: AddressInfo ;

  public ngOnInit(): void {

    this.source = this.source || "";
    this.selectedPlanningBlocks = this.selectedPlanningBlocks || [];

    if (this.selectedPlanningBlocks.length > 0) {
      const containerPoolRequestNo = this.selectedPlanningBlocks[0].containerPoolRequestNo;
      if (containerPoolRequestNo && containerPoolRequestNo !== "") {
        this.isPendingRequest = true;
        this.selectedContainerPoolRequestInfoStatus = 1;
      }
    }

    this.applicationUser$.subscribe((user) => {
      if (user !== null) {
        this.enableAutocomplete = user.generalSetting.enableAutocomplete;
        this.setAllowedAddresses(user.generalSetting.moveToPoolAllowedAddresses);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public moveToPool(): void {
    const account = this.authService.getAccount();

    if (this.isContainerPoolRequest()) {
      this.requestContainerPoolRequest();
    } else {
      const containerPoolRequestNumbers: string[] = this.selectedPlanningBlocks.map((x) => x.containerPoolRequestNo);

      if (containerPoolRequestNumbers.length > 0) {
        switch (this.selectedContainerPoolRequestInfoStatus) {
          case ContainerReuseStatus.APPROVED:
            this.updateContainerPoolRequestStateToApproved(containerPoolRequestNumbers);
            break;
          case ContainerReuseStatus.REJECTED:
            this.updateContainerPoolRequestStateToRejected(containerPoolRequestNumbers);
            break;
          case ContainerReuseStatus.CANCELED:
            this.updateContainerPoolRequestStateToCanceled(containerPoolRequestNumbers);
            break;
        }
      } else {
        this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.MOVE_IN_OUT_OF_POOL_FAILED");
      }
    }
  }

  public requestContainerPoolRequest(): void {
    const account = this.authService.getAccount();
    if (account.username) {
      this.loading = true;
      this.poolService.registerApprovePoolContReqChecks(this.selectedPlanningBlocks.map((x) => x.id), account.username)
        .subscribe(
          () => {
            this.registerContainerPoolRequestServiceCall(account.username);
          }, _ => {
            this.resetUi();
          }, () => {
            this.loading = false;
          });
    } else {
      this.resetUi();
    }
  }

  public registerContainerPoolRequestServiceCall(username: string): void {
    this.loading = true;
    this.sharedService.registerContainerToPoolRequest(this.selectedPlanningBlocks.map((x) => x.id),
      username,
    ).subscribe((planningBlocks: PlanningBlock[]) => {
      this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);
      this.onSuccessEvents("TOASTR_MESSAGES.SUCCESS.CONTAINER_POOL_REQUEST_REGISTRATION_COMPLETED_SUCCESFULLY");
    }, _ => {
      this.resetUi();
    }, () => {
      this.loading = false;
    });
  }

  public updateContainerPoolRequestStateToApproved(poolContainerRequestNumbers: string[]): void {
    const account = this.authService.getAccount();
    if (account.username) {
      this.updateContainerPoolRequestStateToApprovedServiceCall(poolContainerRequestNumbers, account.username);
    } else {
      this.resetUi();
    }
  }

  public updateContainerPoolRequestStateToApprovedServiceCall(
    poolContainerRequestNumbers: string[],
    username: string): void {
    this.loading = true;
    this.sharedService
      .approveContainerToPoolRequest(poolContainerRequestNumbers, this.selectedAdrsNumber.number, username)
      .subscribe(
        (planningBlocks: PlanningBlock[]) => {
          this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);
          this.onSuccessEvents("TOASTR_MESSAGES.SUCCESS.CONTAINER_POOL_REQUEST_ACCEPTED_COMPLETED_SUCCESFULLY");
        },
        () => {
          this.resetUi();
        }, () => {
          this.loading = false;
        }
      );
  }

  public updateContainerPoolRequestStateToRejected(
    containerPoolRequestNumbers: string[]): void {
    const account = this.authService.getAccount();
    if (account.username) {
      this.updateContainerPoolRequestStateToRejectedServiceCall(
        containerPoolRequestNumbers,
        account.username,
      );
    } else {
      this.resetUi();
    }
  }

  public updateContainerPoolRequestStateToRejectedServiceCall(
    containerPoolRequestNumbers: string[],
    username: string): void {
    this.loading = true;
    this.sharedService
      .rejectContainerToPoolRequest(containerPoolRequestNumbers, username)
      .subscribe(
        (planningBlocks: PlanningBlock[]) => {
          this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);
          this.onSuccessEvents("TOASTR_MESSAGES.SUCCESS.CONTAINER_POOL_REQUEST_REJECTED_COMPLETED_SUCCESFULLY");
        },
        () => {
          this.resetUi();
        }, () => {
          this.loading = false;
        }
      );
  }

  public updateContainerPoolRequestStateToCanceled(
    containerPoolRequestNumbers: string[]): void {
    const account = this.authService.getAccount();
    if (account.username) {
      this.updateContainerPoolRequestStateToCanceledServiceCall(
        containerPoolRequestNumbers,
        account.username,
      );
    } else {
      this.resetUi();
    }
  }

  public updateContainerPoolRequestStateToCanceledServiceCall(
    containerPoolRequestNumbers: string[],
    username: string): void {
    this.loading = true;
    this.sharedService
      .cancelContainerToPoolRequest(containerPoolRequestNumbers, username)
      .subscribe(
        (planningBlocks: PlanningBlock[]) => {

          this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);
          this.onSuccessEvents("TOASTR_MESSAGES.SUCCESS.CONTAINER_POOL_REQUEST_CANCELED_COMPLETED_SUCCESFULLY");

        },
        () => {
          this.resetUi();
        }, () => {
          this.loading = false;
        }
      );
  }

  public isContainerPoolRequest(): boolean {
    return !this.isPendingRequest;
  }

  public onSuccessEvents(messageTranslationKey: string): void {
    this.modalManager.closeModal(this.modalType.moveToPool);
    this.selectedContainerPoolRequestInfoStatus = 0;
    this.sharedActions.resetModalDomains();
    this.userFeedbackService.showSuccessMessage(messageTranslationKey);

    this.onMoveToPoolSucceeded.emit(true);
    this.close();
  }

  public resetUi(): void {

    this.sharedActions.setUiState(true);
    this.sharedActions.resetModalDomains();
    this.modalManager.closeModal(this.modalType.moveToPool);
    this.close();
  }

  public showElement(): boolean {
    return this.selectedContainerPoolRequestInfoStatus === 1;
  }

  public disableMoveButton(): boolean {
    return this.isPendingRequest
      && this.selectedContainerPoolRequestInfoStatus === 1
      && this.isInvalidateAddress();
  }

  public addressListValueFormatter(address: AddressInfo) {
    return `${address.name} - ${address.searchName}`;
  }

  private setAllowedAddresses(moveToPoolAllowedAddresses: string) {
    if (!moveToPoolAllowedAddresses || moveToPoolAllowedAddresses.length === 0) {
      this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.MOVE_IN_OUT_OF_POOL_MISSING_ADDRESS_CONFIG");
      return;
    }

    const filter = {
      "No":  moveToPoolAllowedAddresses,
    };

    this.sharedService.getAddressFiltered(filter)
      .subscribe(
      (result: AddressInfo[]) => {
        if (result && result.length > 0) {
          this.addressList = result;
        } else {
          this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.MOVE_IN_OUT_OF_POOL_MISSING_ADDRESS_CONFIG");
        }
      });
  }

  public isInvalidateAddress(): boolean {
    return !this.selectedAdrsNumber || !this.selectedAdrsNumber.number;
  }

  public clearAddress() {
    this.selectedAdrsNumber = null;
  }
}
